import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, CloudUpload, Loader2, RotateCw, X } from "lucide-react";
import { initialsOf } from "../lib/datetime";
import type { AttendanceStatus, RosterEntry } from "../types";
import type { OutboxEntry } from "../lib/outbox";

export interface RosterRowProps {
  entry: RosterEntry;
  status: AttendanceStatus;
  /** Edited locally, not yet submitted. */
  dirty: boolean;
  /** Submitted and in the outbox — delivery state, distinct from `dirty`. */
  sync?: OutboxEntry;
  disabled: boolean;
  onChange: (status: "present" | "absent") => void;
  onRetry: () => void;
}

const baseToggle =
  "flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-all disabled:opacity-60";

export function RosterRow({
  entry,
  status,
  dirty,
  sync,
  disabled,
  onChange,
  onRetry,
}: RosterRowProps) {
  const { t } = useTranslation();
  const name = entry.name || entry.assigned_id;
  const reasonCode = sync?.status === "absent" ? sync.reasonCode : entry.absence_reason_code;
  const reasonDetail = sync?.status === "absent" ? sync.reasonDetail : entry.absence_reason_detail;

  return (
    <li className="flex items-center justify-between gap-2 rounded-xl bg-surface p-2.5 shadow-raised">
      <div className="flex min-w-0 items-center gap-3 pe-1">
        <span className="relative grid h-11 w-11 shrink-0 place-items-center rounded-full bg-app-field text-sm font-semibold text-app-ink">
          {initialsOf(entry.name, entry.assigned_id)}
          {entry.online && (
            <span
              className="absolute -end-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-surface bg-success"
              title={t("online")}
            />
          )}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5">
            <h3 className="truncate text-base font-semibold text-app-ink">{name}</h3>
            {dirty && !sync && (
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                title={t("unsubmittedChange")}
              />
            )}
          </span>
          {sync && (
            <p
              className={`flex items-center gap-1 truncate text-xs font-medium ${
                sync.state === "failed" ? "text-accent-strong" : "text-accent"
              }`}
            >
              {sync.state === "syncing" && <Loader2 size={12} className="animate-spin" />}
              {sync.state === "queued" && <CloudUpload size={12} />}
              {sync.state === "failed" && <AlertTriangle size={12} />}
              {sync.state === "failed"
                ? t("syncFailedRow")
                : sync.state === "syncing"
                  ? t("syncSyncing")
                  : t("syncQueued")}
            </p>
          )}
          {status === "absent" && reasonCode && (
            <p className="text-xs text-app-muted">
              {t("absenceReasonLabel")}:{" "}
              {reasonCode === "other"
                ? reasonDetail
                : t(`absenceReason_${reasonCode}`) + (reasonDetail ? ` – ${reasonDetail}` : "")}
            </p>
          )}
        </span>
      </div>

      {sync?.state === "failed" && (
        <button
          type="button"
          className="flex shrink-0 items-center gap-1 rounded-full bg-danger-bg px-2.5 py-1 text-xs font-semibold text-accent-strong"
          onClick={onRetry}
        >
          <RotateCw size={12} />
          {t("retry")}
        </button>
      )}

      <div
        className="flex shrink-0 items-center rounded-full bg-app-field p-1"
        role="group"
        aria-label={t("attendanceFor", { name })}
      >
        <button
          type="button"
          className={`${baseToggle} ${
            status === "present" ? "bg-success text-content-on-success" : "text-app-muted"
          }`}
          onClick={() => onChange("present")}
          disabled={disabled}
          aria-pressed={status === "present"}
        >
          {status === "present" && <Check size={14} />}
          {t("present")}
        </button>
        <button
          type="button"
          className={`${baseToggle} ${
            status === "absent" ? "bg-danger-bg text-accent-strong" : "text-app-muted"
          }`}
          onClick={() => onChange("absent")}
          disabled={disabled}
          aria-pressed={status === "absent"}
        >
          {status === "absent" && <X size={14} />}
          {t("absent")}
        </button>
      </div>
    </li>
  );
}
